import { isRef, type UnwrapNestedRefs } from 'vue'
import { isDate, isFactor, isFunction, isNullish } from '../is'
import type { TAnyObject, StringIndexable } from '@/types/object'
import { unref } from '../reactive'
import { range, unique } from '../array'
import type { Dict } from '@/types/dict'

export const propagateDown = <T extends { [key: string]: any } = {}>(
  obj: T,
  keys: string[],
  value: any
): T => {
  if (keys.length <= 0) return obj
  if (isRef(value)) value = value.value

  const keyz = keys as (keyof T)[]
  const key = keyz[0]

  if (isRef(obj)) {
    obj = obj.value as T
  }

  if (!(key in obj)) {
    obj[key] = {} as T[keyof T]
  }

  if (key in obj) {
    if (isFactor(obj[key])) {
      obj[key] = value
    } else if (isRef(obj[key])) {
      obj[key].value = value
    } else if (typeof obj[key] === 'object') {
      obj[key] = propagateDown(obj[key], keys.splice(1), value)
    }
  }

  return obj
}

export const select = <T, Output = any>(
  obj: T,
  path: string,
  unwrapRef: boolean = true
): Output | undefined => {
  const keys = path.split('.')
  function traverse(current: any, index: number): any {
    if (current === null || index >= keys.length) {
      if (isRef(current) && unwrapRef) return current.value
      return current
    }

    if (!(keys[index] in current)) return undefined
    return traverse(current[keys[index]], index + 1)
  }
  return traverse(obj, 0)
}

export const selectAndSet = <T>(
  obj: T,
  path: string,
  value: any,
  createIfNotExists?: boolean
): T => {
  const keys = path.split('.')
  function set(obj: any, index: number): void {
    if (index === keys.length - 1) {
      obj[keys[index]] = value
      return
    }
    if (!obj[keys[index]] || typeof obj[keys[index]] !== 'object') {
      if (createIfNotExists) {
        obj[keys[index]] = {}
      } else {
        return obj
      }
    }
    set(obj[keys[index]], index + 1)
    return obj
  }

  set(obj, 0)
  return obj
}


export const patchObject = (obj: any, path: string, value: any) => {
  const keys = path.split('.');

  const traverse = (obj: any, keys: string[]) => {
    if (!Array.isArray(obj) && typeof obj !== 'object') return;
    const key = keys[0];
    if (typeof (key as any) === 'undefined' || (key as any) === null) return;
    if (keys.length === 1 && (typeof obj === 'object' || Array.isArray(obj))) {
      if (Array.isArray(obj)) {
        const num = Number(key);
        obj[num] = (typeof value === 'undefined' || value === null) ? null : value;
        obj = obj.filter(it => it !== null && typeof it !== 'undefined');
        return;
      }
      obj[key] = value;
      return;
    }

    if (keys.length <= 0) return;
    traverse(obj[key], keys.slice(1));
  }

  traverse(obj, keys);

  return obj;
}

export const copy = <T extends { [key: string]: any }>(obj?: T): T => {
  if (!obj) return {} as T
  if (isRef(obj)) obj = obj.value as T
  const deepClone = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map(deepClone)
    } else if (input instanceof Date) {
      return new Date(input)
    } else if (typeof input === 'object' && input !== null) {
      const clonedObject: { [key: string]: any } = {}
      for (const key in input) {
        if (Object.prototype.hasOwnProperty.call(input, key)) {
          clonedObject[key] = deepClone(input[key])
        }
      }
      return clonedObject
    } else {
      return input
    }
  }

  const clonedObj = deepClone(obj)
  return clonedObj
}

export const unwrap = <T extends { [key: string]: any }>(obj?: T): T => {
  if (!obj) return {} as T

  try {
    return JSON.parse(JSON.stringify(obj)) as T
  } catch (e) {
    return copy<T>(obj)
  }
}

const canUnwrap = (item: any): item is StringIndexable => {
  if (!item) return false
  if (typeof item !== 'object') return false
  if (isDate(item)) return false
  if (isFunction(item)) return false
  return true
}

export const unwrapNestedRefs = <T extends { [key: string]: any } = { [key: string]: any }>(
  obj: T
): UnwrapNestedRefs<T> => {
  if (!obj) return obj;
  return Object.assign(
    {},
    ...Object.entries(obj).map(([key, value]) => {
      const unwrapped = isRef(value) ? unref(value) : value
      return {
        [key]: canUnwrap(unwrapped) ? unwrapNestedRefs(unwrapped) : unwrapped
      }
    })
  )
}

type SetFieldFunction<R, LT extends TAnyObject = TAnyObject> = (
  key: string,
  newKey: string,
  value: any,
  lookup?: LT
) => R

const unwrapToPaths_ = <
  T extends { [key: string]: any } = {},
  SetKeyR = any,
  LT extends TAnyObject = TAnyObject
>(
  obj: T,
  setField?: SetFieldFunction<SetKeyR, LT>,
  lookup?: LT,
  parentKey: string = '',
  parentIsArray: boolean = false,
  result: { [key: string]: any } = {},
  maxDepth: number = -1,
  depth: number = 0
): { [key: string]: SetKeyR } => {
  if (maxDepth >= 0 && depth > maxDepth) return result as { [key: string]: SetKeyR }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = parentIsArray
        ? `${parentKey}[${key}]`
        : parentKey
          ? `${parentKey}.${key}`
          : key

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        unwrapToPaths_(
          obj[key],
          setField,
          lookup ? lookup[key] : undefined,
          newKey,
          false,
          result,
          maxDepth,
          depth + 1
        )
      } else if (Array.isArray(obj[key])) {
        const arr = obj[key] as any[]
        for (let i = 0; i < arr.length; i++) {
          unwrapToPaths_(
            obj[key],
            setField,
            lookup ? lookup[key] : undefined,
            newKey,
            true,
            result,
            maxDepth,
            depth + 1
          )
        }
      } else {
        result[newKey] = setField ? setField(key, newKey, obj[key], lookup) : obj[key]
      }
    }
  }
  return result as { [key: string]: SetKeyR }
}

export const unwrapToPaths = <
  T extends { [key: string]: any } = {},
  SetKeyR = any,
  LT extends TAnyObject = TAnyObject
>(
  obj: T,
  setField?: SetFieldFunction<SetKeyR, LT>,
  lookup?: LT,
  maxDepth: number = -1
) => {
  return unwrapToPaths_<T, SetKeyR, LT>(obj, setField, lookup, '', false, {}, maxDepth, 0)
}

type PatchOptions = {
  mutatePrimitives?: boolean
}

const defaultPatchOptions: PatchOptions = { mutatePrimitives: true }

export const assign = <T extends { [key: string]: any } = { [key: string]: any }>(
  a: T | undefined | null,
  ...args: { [key: string]: any }[]
) => {
  return Object.assign(a || {}, ...args)
}

export const assignSame = <T extends { [key: string]: any } = { [key: string]: any }>(
  a: T | undefined | null,
  b: Partial<T> | (() => Partial<T>)
): T => {
  return Object.assign(a || {}, isFunction(b) ? b() : b) as unknown as any as T
}

export const patch = (a: any, b: any, options: PatchOptions = defaultPatchOptions): any => {
  if (typeof a === 'undefined' || a === null) return b
  if (options.mutatePrimitives === false && isFactor(a)) return a
  if (isFactor(a) && isFactor(b)) return b

  if (Array.isArray(a) && Array.isArray(b)) {
    const n = Math.max(a.length, b.length)
    const r = range(n)

    return r.map((i) => {
      const ia = i < a.length ? a[i] : undefined
      const ib = i < b.length ? b[i] : undefined
      return patch(ia, ib, options)
    })
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const ksa = Object.keys(a)
    const ksb = Object.keys(b)
    const r = range(Math.max(ksa.length, ksb.length))
    const keys: string[] = unique(
      r.map((i) => {
        const ka = i < ksa.length ? ksa[i] : undefined
        const kb = i < ksb.length ? ksb[i] : undefined
        return ka || kb || ''
      })
    )

    return keys.reduce((pk, nk) => {
      return {
        ...pk,
        [nk]: patch(a[nk], b[nk], options)
      }
    }, {})
  }

  return a
}

export const omit = <T extends Dict, K extends ((keyof T) | (string & {}))>(obj: T, keys: K[]): Omit<T, K> => {
  const next = {...obj} as any;
  for (const key of keys) {
    delete next[key]
  }
  return next as Omit<T, K>;
}

export const getPaths = (
  obj: StringIndexable,
  base?: string,
  filter?: (path: string) => boolean
): string[] => {
  const filterFun = filter || (() => true)

  let paths: string[] = []
  Object.keys(obj)
    .filter(filterFun)
    .forEach((key) => {
      const value = obj[key]
      const fullPath = base ? `${base}.${key}` : key
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const next = getPaths(value, fullPath)
        if (next.length <= 0) {
          paths.push(fullPath)
        } else {
          paths = paths.concat(next)
        }
      } else {
        paths.push(fullPath)
      }
    })
  return paths
}

export const getKeysRecursively = (
  obj: StringIndexable,
  filter: (k: string) => boolean = () => true
): string[] => {
  const getKeys = (obj: StringIndexable, keys: string[] = []) => {
    keys = [...keys, ...Object.keys(obj).filter(filter)]

    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        keys = [...keys, ...getKeys(value, keys)]
      }
    }

    return unique(keys)
  }
  return getKeys(obj)
}


export const hasSomeKeys = (obj: StringIndexable, keys: string[]): boolean => {
  return Object.keys(obj).some(k => keys.includes(k));
}

export const mergeDicts = <T extends StringIndexable = StringIndexable>(a: T, b: T): T => {
  const entries = Object.entries(b) as Array<[keyof typeof a, any]>;

  for (const [key, value] of entries) {
    if (!(key in a)) {
      a[key] = value;
      continue;
    }
    const aValue = a[key];
    const bValue = b[key];
    if (typeof aValue !== typeof bValue) continue;

    if (typeof aValue === 'function' || typeof bValue === 'function') continue;

    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      (a as any)[key] = [...aValue, ...bValue];
      continue;
    }

    if (typeof aValue === 'object' && typeof bValue === 'object') {
      a[key] = mergeDicts(aValue, bValue);
      continue;
    }
  }

  return a;
}


export const mergeObjects = (a: any, b: any): any => {
  a = isRef<Dict>(a) ? unref(a) : a
  b = isRef<Dict>(b) ? unref(b) : b

  const next = { ...a, ...b }

  for (const key of Object.keys(b)) {
    if (isNullish(a[key]) && !isNullish(b[key])) {
      next[key] = b[key]
      continue
    }
    if (isNullish(b[key])) continue
    if (typeof a[key] === 'object' && typeof b[key] === 'object') {
      next[key] = mergeDicts(a[key], b[key])
      continue
    }
    next[key] = b[key]
  }

  return next
}
